import React from "react"
import { TransferRequestsButton } from "../../components/transferRequests/TransferRequests"   // tog'a #3151

import useQueryParams from "../../hooks/useQueryParams"
import safeObj from "../../utils/safeObj"

import Page from "../../containers/page"
import GroupedPage from "../../containers/page/GroupedPage"
import Drawer from "../../components/drawer"
import { ITableExtended } from "../../components/table/TableExtended"
import {
  GET_ALL_FILTERED_TRANSFERS,
  GET_ALL_TRANSFORM,
  GET_FILTERED_TRANSFERS,
  GET_TRANSFORM,
  GET_TRANSFER_SHORTAGES,
} from "./queries"
import TransferForm from "./TransferForm"
import TransferEditModal from "./TransferEditModal"   // tog'a #3177
import { columns, groupColumns, shortageColumns } from "./data"
import { Search, AlertTriangle } from "react-feather"
import { Button, DatePicker } from "antd"
import { useTranslation } from "react-i18next"
import FilterModal from "./filters/FilterModal"
import moment from "moment"
import getDateInterval from "../../utils/getDateInterval"
import SearchInput from "../../components/Search"

export default function Transfers({
  tableProps,
  pageQuery,
  pageName,
}: {
  tableProps?: Partial<ITableExtended>
  pageQuery?: any
  pageName?: string
}) {
  const query = useQueryParams()
  const name = pageName || "transfers"
  const { t } = useTranslation()

  const handleDateChange = (dates: any) => {
    if (!dates || !dates[0] || !dates[1]) {
      query.removeMany("begin", "end")
    } else {
      const { begin, end } = getDateInterval(dates[0], dates[1])
      query.set("begin", begin)
      query.set("end", end)
    }
  }

  // Kun tafsiloti (drawer) uchun tanlangan kun
  const selectDay = query.secureGet("selectDay")?.toString() || ""

  const leftButtons: JSX.Element[] = [
    <SearchInput name={name} key="search" className="headermargin_r" />,
    <TransferRequestsButton key="transfer-requests" className="hdr-pin hdr-pin-only headermargin_r" />,   // tog'a #3151: zakaz qoldig'i — asosiy skladdan so'rovlar
    <DatePicker.RangePicker
      key="date-picker"
      allowClear
      style={{ width: "240px", marginRight: "8px" }}
      size="large"
      format="DD.MM.YYYY"
      placeholder={[t("startDate") || t("startLbl"), t("endDate") || t("endShort")]}
      value={
        query.secureGet("begin") && query.secureGet("end")
          ? [
              moment(query.secureGet("begin").toString()),
              moment(query.secureGet("end").toString()),
            ]
          : null
      }
      onChange={handleDateChange}
      ranges={{
        [t("today") || t("today")]: [moment().startOf("day"), moment().endOf("day")],
        [t("yesterday") || t("yesterday")]: [
          moment().subtract(1, "day").startOf("day"),
          moment().subtract(1, "day").endOf("day"),
        ],
        [t("thisWeek") || t("thisWeekLbl")]: [
          moment().startOf("week"),
          moment().endOf("week"),
        ],
        [t("thisMonth") || t("thisMonth")]: [
          moment().startOf("month"),
          moment().endOf("month"),
        ],
      }}
    />,
    <Button
      key="filter"
      size="large"
      style={{ width: "130px" }}
      className="table-action-btn headermargin_r"
      icon={<Search key="search" />}
      onClick={() => query.set("filtering", "true")}
    >
      {t("filter")}
    </Button>,
    // Boshliq (2026-09-03, 6-qaror): perevodda kam chiqqan tovarlar ro'yxati.
    // Alohida menyu bo'limi qilinmadi — bu perevodning bir qismi, shuning uchun
    // shu sahifada panel sifatida ochiladi va mavjud «transfers» huquqidan foydalanadi.
    <Button
      key="shortages"
      size="large"
      className="table-action-btn headermargin_r"
      icon={<AlertTriangle size={16} />}
      onClick={() => query.set("shortages", "true")}
    >
      {t("transferShortages")}
    </Button>,
  ]

  // Kun tafsiloti (yoki tableProps bilan chaqirilganda oddiy ro'yxat)
  const detailPage = (
    <>
    <TransferEditModal />
    <Page
      name={name}
      singularName={"transfer"}
      collectionQuery={pageQuery || GET_TRANSFORM}
      exportQuery={GET_ALL_TRANSFORM}
      queryVariables={safeObj({
        ...(selectDay
          ? { begin: selectDay, end: selectDay }
          : query.secureGet("begin") &&
            query.secureGet("end") && {
              begin: query.secureGet("begin"),
              end: query.secureGet("end"),
            }),
        warehouseFromId: +query.secureGet("warehouseFromId") > 0 ? +query.secureGet("warehouseFromId") : undefined,
        warehouseToId: +query.secureGet("warehouseToId") > 0 ? +query.secureGet("warehouseToId") : undefined,
        searchText: query.secureGet("searchText") || query.secureGet("search") || "",
      })}
      formWidth={1180}
      formComponent={<TransferForm />}
      filterComponent={FilterModal}
      columns={columns}
      drawerProps={{
        // tog'a #2262 (2026-09-21): tovar jadvalida 11 ta ustun bor — 700px da «Кол-во» o'ngda ko'rinmay qolardi.
        // Kengroq oyna: ustunlar siqilmaydi va son maydoni gorizontal surishsiz ko'rinadi (kichik ekranda ekran bo'yicha).
        width: typeof window !== "undefined" ? Math.min(1120, window.innerWidth - 32) : 1120,
        // Msg 263 audit: avto-ochilishda forma-drawer selectDay (100%, z1000)
        // draweri OSTIDA qolib bosilmas edi — forma qatlami konvensiya bo'yicha 1010
        zIndex: 1010,
      }}
      tableProps={{
        ...tableProps,
        leftButtons: selectDay ? undefined : leftButtons,
        onRow: (data: any) => ({
          onDoubleClick: () => {},
        }),
        size: "small",
        summaryKeys: ["transferCount"],
        hideImport: true,
        hideExport: true,
        hideDelete: true,
      }}
    />
    </>
  )

  // tableProps berilgan bo'lsa (masalan boshqa sahifa ichida) — faqat oddiy jadval
  return !tableProps ? (
    <>
      <GroupedPage
        name="transfersByFilter"
        singularName={"transferDay"}
        exportQuery={GET_ALL_FILTERED_TRANSFERS}
        exportQueryName="transfersByFilter"
        collectionQuery={GET_FILTERED_TRANSFERS}
        columns={groupColumns}
        queryVariables={safeObj({
          ...(query.secureGet("begin") &&
            query.secureGet("end") && {
              begin: query.secureGet("begin"),
              end: query.secureGet("end"),
            }),
          warehouseFromId: +query.secureGet("warehouseFromId") > 0 ? +query.secureGet("warehouseFromId") : undefined,
          warehouseToId: +query.secureGet("warehouseToId") > 0 ? +query.secureGet("warehouseToId") : undefined,
          searchText: query.secureGet("searchText") || query.secureGet("search") || "",
        })}
        filterComponent={FilterModal}
        tableProps={{
          // Kun ustiga ikki marta bosilganda o'sha kunning perevodlari ochiladi
          onRow: (data: any) => ({
            onDoubleClick: () => {
              if (data?.date) {
                query.set("selectDay", moment(data.date).format("YYYY-MM-DD"))
              }
            },
          }),
          // Qo'shish — bugungi kun tafsilotini ochamiz (ichida qo'shish tugmasi bor)
          // Msg 220: Добавить bosilganda perevod formasi DARHOL ochiladi
          onAdd: () =>
            query.append({
              selectDay: moment().format("YYYY-MM-DD"),
              transfer: "add",
            }),
          summaryKeys: ["transfersCount", "totalAmount"],
          isGroup: true,
          hideImport: true,
          hideExport: true,
          hideDelete: true,
          size: "small",
          leftButtons: leftButtons,
        }}
      />
      <Drawer placement={"left"} width="100%" matcher="selectDay">
        {detailPage}
      </Drawer>

      {/* Boshliq (2026-09-03, 6-qaror): «Kam chiqdi» — jo'natilgandan kam qabul
          qilingan perevodlar. Ma'lumot mainTransferLog da allaqachon bor edi
          (TransferCount / AcceptedCount), lekin hech qayerda ko'rinmasdi. */}
      <Drawer removePaddingX placement={"left"} width="100%" matcher="shortages">
        <Page
          name="transferShortages"
          singularName={"transferShortage"}
          collectionQuery={GET_TRANSFER_SHORTAGES}
          queryVariables={safeObj({
            ...(query.secureGet("begin") &&
              query.secureGet("end") && {
                begin: query.secureGet("begin"),
                end: query.secureGet("end"),
              }),
          })}
          columns={shortageColumns}
          tableProps={{
            size: "small",
            hideImport: true,
            hideDelete: true,
            summaryKeys: ["sent", "accepted", "shortage"],
          }}
        />
      </Drawer>
    </>
  ) : (
    detailPage
  )
}
